import { FC, useState } from 'react'
import { ReviewsByPlatformItem } from 'src/services/reviewAnalysisApi/interfaces/reviewAnalysis.interface'
import styles from './sentimentChart.module.scss'
import { getPlatformIcon } from '../../../platformIcons'

interface SentimentChartProps {
  platforms: ReviewsByPlatformItem[]
}

type SentimentKey = 'negative' | 'neutral' | 'positive'

const SENTIMENT_LABEL: Record<SentimentKey, string> = {
  negative: 'Negative',
  neutral: 'Neutral',
  positive: 'Positive',
}

// Stack order top → bottom (rendered as a column): neutral, negative, positive.
const STACK: SentimentKey[] = ['neutral', 'negative', 'positive']

// Hide the inline % label on slivers where it wouldn't fit.
const MIN_LABEL_PCT = 8

interface HoveredSegment {
  platform: string
  sentiment: SentimentKey
}

export const SentimentChart: FC<SentimentChartProps> = ({ platforms }) => {
  const [hovered, setHovered] = useState<HoveredSegment | null>(null)

  return (
    <div className={styles.chartCard}>
      <div className={styles.plotArea}>
        <div className={styles.yAxis}>
          <span>100%</span>
          <span>50%</span>
          <span>0%</span>
        </div>

        <div className={styles.barsArea}>
          <div className={styles.gridLines}>
            <div className={styles.gridLine} />
            <div className={styles.gridLine} />
            <div className={styles.gridLineBase} />
          </div>

          {platforms.map((platform) => {
            const counts = platform.sentiment ?? {
              negative: 0,
              neutral: 0,
              positive: 0,
            }
            const total = counts.positive + counts.negative + counts.neutral
            const pct = (key: SentimentKey) =>
              total > 0 ? Math.round((counts[key] / total) * 100) : 0
            const Icon = getPlatformIcon(platform.platform)

            // Tooltip sits above the hovered segment's vertical center
            // (segments stack bottom-up: positive, then negative, then neutral).
            const hoveredKey =
              hovered?.platform === platform.platform ? hovered.sentiment : null
            const centerFromBottom = (key: SentimentKey) => {
              if (key === 'positive') return pct('positive') / 2
              if (key === 'negative')
                return pct('positive') + pct('negative') / 2
              return pct('positive') + pct('negative') + pct('neutral') / 2
            }

            return (
              <div className={styles.barColumn} key={platform.platform}>
                {hoveredKey && (
                  <div
                    className={styles.tooltip}
                    style={{ bottom: `${centerFromBottom(hoveredKey)}%` }}
                  >
                    <div className={styles.tooltipHeader}>
                      <Icon className={styles.tooltipIcon} />
                      <span>{platform.name}</span>
                    </div>
                    <div className={styles.tooltipBody}>
                      {SENTIMENT_LABEL[hoveredKey]} Reviews:{' '}
                      <strong>{counts[hoveredKey].toLocaleString()}</strong>
                    </div>
                  </div>
                )}

                <div className={styles.barTrack}>
                  {total > 0 &&
                    STACK.map((key) => {
                      const value = pct(key)
                      if (value === 0) return null
                      return (
                        <div
                          className={`${styles.segment} ${styles[key]}`}
                          key={key}
                          onMouseEnter={() =>
                            setHovered({
                              platform: platform.platform,
                              sentiment: key,
                            })
                          }
                          onMouseLeave={() => setHovered(null)}
                          style={{ height: `${value}%` }}
                        >
                          {value >= MIN_LABEL_PCT && (
                            <span className={styles.segmentLabel}>
                              {value}%
                            </span>
                          )}
                        </div>
                      )
                    })}
                </div>

                <div className={styles.xLabel}>
                  <Icon className={styles.xLabelIcon} />
                  <span>{platform.name}</span>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <div className={styles.legend}>
        {STACK.slice()
          .reverse()
          .map((key) => (
            <div className={styles.legendItem} key={key}>
              <span className={`${styles.legendDot} ${styles[key]}`} />
              {SENTIMENT_LABEL[key]}
            </div>
          ))}
      </div>
    </div>
  )
}
